/**
 * A floorplan read off an image, before it becomes layers.
 *
 * What the image tracer produces, kept apart from how it becomes a plan so the two can be tested
 * on their own. Coordinates are normalised to the image:
 * 0 to 1000 across its width (`x`, left to right) and 0 to 1000 down its height (`y`, top to
 * bottom), whatever its proportions.
 *
 * Lengths — a wall's thickness, a desk's size, a door's width — are in the units of `x`: a
 * thousandth of the image's width. `y` is stretched by the image's proportions and `x` is not,
 * so `x` is the one that measures the same in every direction.
 */

export interface SketchPoint {
  readonly x: number;
  readonly y: number;
}

export interface SketchRoom {
  readonly name?: string | null;
  readonly outline: readonly SketchPoint[];
  /** How sure the tracer is, 0 to 1. Absent for what a person drew or marked. */
  readonly confidence?: number;
}

/** Where a wall sits in the building. */
export type SketchWallKind = 'exterior' | 'interior' | 'partition';

export interface SketchWall {
  readonly from: SketchPoint;
  readonly to: SketchPoint;
  /** As drawn, in the units of `x`. Absent when the drawing is only lines. */
  readonly thickness?: number | null;
  readonly kind?: SketchWallKind;
  /** How sure the tracer is, 0 to 1. Absent for what a person drew or marked. */
  readonly confidence?: number;
}

export interface SketchDesk {
  readonly x: number;
  readonly y: number;
  /** The direction of the desk's long side: 0 across the image, 90 up and down it. */
  readonly rotationDegrees: number;
  /** The long side and the short side as drawn, in the units of `x`. */
  readonly width?: number | null;
  readonly depth?: number | null;
  /** How sure the tracer is, 0 to 1. Absent for what a person drew or marked. */
  readonly confidence?: number;
}

/** A box in sketch coordinates. */
export interface SketchBox {
  readonly left: number;
  readonly top: number;
  readonly right: number;
  readonly bottom: number;
}

export interface SketchLabel {
  /** Empty for writing the tracer found but cannot read: the administrator types it in review. */
  readonly text: string;
  readonly x: number;
  readonly y: number;
  /** Where the writing is on the drawing, when it was found there. */
  readonly box?: SketchBox | null;
  /** How sure the tracer is, 0 to 1. Absent for what a person drew or marked. */
  readonly confidence?: number;
}

/**
 * How the tracer turned the picture before reading it, so what it found can be shown over the
 * uploaded image: the image, `sourceWidth` by `sourceHeight`, turned `degrees` clockwise about
 * its middle on a frame `width` by `height`. Sizes are in the tracer's pixels.
 */
export interface SketchFrame {
  readonly degrees: number;
  readonly sourceWidth: number;
  readonly sourceHeight: number;
  readonly width: number;
  readonly height: number;
}

/** A fitting a plan draws a symbol for: furniture on the plan, shown with its icon. */
export type SketchSymbolKind = 'toilet' | 'sink' | 'bathtub' | 'shower' | 'hob';

/** A drawn shape on the floor that is neither a room nor a desk. */
export type SketchAreaKind = 'door' | 'window' | 'stairs' | 'column' | 'furniture' | SketchSymbolKind;

export interface SketchArea {
  readonly kind: SketchAreaKind;
  readonly outline: readonly SketchPoint[];
  /** How sure the tracer is, 0 to 1. Absent for what a person drew or marked. */
  readonly confidence?: number;
}

/**
 * The drawing's own ink, squared up as the sketch is: 1 ink, 0 paper, row by row. A pixel's
 * column over `width` is the sketch's `x` over 1000, its row over `height` its `y`. Kept so that
 * what is not replaced by a find on the plan is kept as drawn (`as-drawn`).
 */
export interface SketchInk {
  readonly width: number;
  readonly height: number;
  readonly data: Uint8Array;
  /** How wide a line is drawn, in pixels, thickened a pixel each side. */
  readonly stroke: number;
}

/**
 * A piece of the drawing kept exactly as drawn: the outline of its ink, then the outline of each
 * hole in it — the paper inside a desk drawn as a rectangle, inside the loop of a letter.
 */
export interface SketchShape {
  readonly rings: readonly (readonly SketchPoint[])[];
}

/** A symbol the drawing shows, placed as an icon. */
export interface SketchIcon {
  readonly iconName: string;
  readonly x: number;
  readonly y: number;
}

/**
 * Where the plan's size came from, from most to least trustworthy: a length the drawing states,
 * the doors (a door is about 0.85 m wide), the desks (a desk is about 1.6 m long), or nothing.
 */
export type SketchScaleSource = 'drawing' | 'doors' | 'desks';

export interface FloorPlanSketch {
  readonly isFloorPlan: boolean;
  readonly border: readonly SketchPoint[];
  readonly rooms: readonly SketchRoom[];
  readonly walls: readonly SketchWall[];
  readonly desks: readonly SketchDesk[];
  readonly labels: readonly SketchLabel[];
  readonly areas?: readonly SketchArea[];
  readonly icons?: readonly SketchIcon[];
  /** How many metres the image's full width covers, when something on it says. */
  readonly metresAcrossImage?: number | null;
  readonly scaleSource?: SketchScaleSource | null;
  /**
   * Height over width of the picture the coordinates are normalised to, when it is not the
   * uploaded image's: straightening a photo taken at an angle makes the picture larger.
   */
  readonly aspect?: number | null;
  readonly frame?: SketchFrame | null;
}

/** The far edge of the normalised coordinates, on both axes. */
export const SKETCH_EXTENT = 1000;
